
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  provide,
  ref,
  watch,
  nextTick,
} from "vue";
import { ElInput, ElButton, ElButtonGroup, ElTooltip } from "element-plus";
import i18n from "@/core/i18n/i18n";

import JsonTreeViewItem, {
  ItemType,
  ItemData,
  ValueTypes,
  SelectedElement,
} from "./JsonTreeViewItem.vue";

// ---------------------------------------------------------------------------
// Small helpers kept local to this component (no shared utility files).
// ---------------------------------------------------------------------------

type SearchMatch = { node: ItemData; path: string };
type ReadablePathSegment = {
  label: string;
  isArrayIndex: boolean;
  isEllipsis?: boolean;
};

function splitIdentifier(
  text: string
): { text: string; breakBefore: boolean }[] {
  const pieces: { text: string; breakBefore: boolean }[] = [];
  let start = 0;
  for (let index = 1; index < text.length; index++) {
    const previous = text[index - 1];
    const current = text[index];
    const next = text[index + 1] || "";
    // Break after a separator (_ . / -) and at camelCase / acronym humps, but
    // NOT between letters and digits, so suffixes like "_L14" stay intact.
    const isBoundary =
      /[_./-]/.test(previous) ||
      (/[a-z]/.test(previous) && /[A-Z]/.test(current)) ||
      (/[A-Z]/.test(previous) &&
        /[A-Z]/.test(current) &&
        /[a-z]/.test(next));
    if (isBoundary) {
      pieces.push({ text: text.slice(start, index), breakBefore: start > 0 });
      start = index;
    }
  }
  pieces.push({ text: text.slice(start), breakBefore: start > 0 });
  return pieces;
}

function removeAtFromPath(path: string): string {
  if (path.length > 1 && path.charAt(1) === "@") {
    return path.slice(0, 1) + path.slice(2);
  }
  return path;
}

// Build the ItemData tree from a parsed JSON value. The path scheme must stay
// identical to the one the renderer relies on for selection.
function build(
  key: string,
  value: ValueTypes,
  depth: number,
  path: string,
  includeKey: boolean,
  parent: ItemData | null = null
): ItemData {
  if (value instanceof Object) {
    const currentParent: ItemData = {
      key,
      type: value instanceof Array ? ItemType.ARRAY : ItemType.OBJECT,
      depth,
      path,
      length: 0,
      children: [],
      parent: parent || undefined,
    };
    const children: ItemData[] = [];
    if (value instanceof Array) {
      value.forEach((element, index) => {
        const childPath = includeKey
          ? `${removeAtFromPath(path)}${key}[${index}]`
          : `${removeAtFromPath(path)}[${index}]`;
        children.push(
          build(index.toString(), element, depth + 1, childPath, false, currentParent)
        );
      });
    } else {
      Object.entries(value).forEach(([childKey, childValue]) => {
        const childPath = includeKey
          ? `${removeAtFromPath(path)}${key}@#`
          : `${removeAtFromPath(path)}@#`;
        children.push(
          build(childKey, childValue, depth + 1, childPath, true, currentParent)
        );
      });
    }
    children.forEach((child) => {
      child.parent = currentParent;
    });
    currentParent.length = children.length;
    currentParent.children = children;
    return currentParent;
  }
  const valuePath = includeKey
    ? `${removeAtFromPath(path)}${key}`
    : removeAtFromPath(path);
  return { key, type: ItemType.VALUE, path: valuePath, depth, value, parent: parent || undefined };
}

// Normalize parsed numeric values so the search tree renders the same way the
// lazy dialogs prepare their chunks without rewriting text inside JSON strings.
function normalizeNumbersForDisplay(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeNumbersForDisplay);
  }
  if (value !== null && typeof value === "object") {
    const normalized: Record<string, unknown> = {};
    Object.entries(value).forEach(([key, child]) => {
      normalized[key] = normalizeNumbersForDisplay(child);
    });
    return normalized;
  }
  return typeof value === "number" ? String(value) : value;
}

function parseToTree(
  data: string | null | undefined,
  rootKey: string,
  normalizeNumbers = false
): ItemData {
  if (data !== null && data !== undefined) {
    try {
      const parsedJson = JSON.parse(data);
      const displayJson = normalizeNumbers
        ? normalizeNumbersForDisplay(parsedJson)
        : parsedJson;
      if (displayJson instanceof Object) {
        return build(
          rootKey,
          Array.isArray(displayJson) ? displayJson : { ...displayJson },
          0,
          "",
          true
        );
      }
    } catch (e) {
      // fall through to scalar handling
    }
  }
  return { key: rootKey, type: ItemType.VALUE, path: "", depth: 0, value: data };
}

function valueToSearchString(value: ValueTypes): string | null {
  if (value === null) {
    return "null";
  }
  const type = typeof value;
  if (type === "string") {
    return value as string;
  }
  if (type === "number" || type === "bigint" || type === "boolean") {
    return String(value);
  }
  return null;
}

// Array element keys are positional indices and the synthetic root is skipped,
// so neither participates in key matching.
function isKeySearchable(node: ItemData): boolean {
  if (node.depth === 0) {
    return false;
  }
  return !(node.parent && node.parent.type === ItemType.ARRAY);
}

// Walk the complete tree in pre-order and collect matching nodes so previous /
// next navigation follows top-to-bottom visual order.
function collectMatches(
  root: ItemData | null | undefined,
  rawQuery: string
): SearchMatch[] {
  const results: SearchMatch[] = [];
  const query = rawQuery.trim().toLowerCase();
  if (!root || query === "") {
    return results;
  }
  const visit = (node: ItemData): void => {
    let hit = false;
    if (isKeySearchable(node) && typeof node.key === "string") {
      hit = node.key.toLowerCase().includes(query);
    }
    if (!hit && node.type === ItemType.VALUE) {
      const valueString = valueToSearchString(node.value);
      hit = valueString !== null && valueString.toLowerCase().includes(query);
    }
    if (hit) {
      results.push({ node, path: node.path });
    }
    if (node.children) {
      node.children.forEach(visit);
    }
  };
  visit(root);
  return results;
}

function getReadablePathSegments(node: ItemData): ReadablePathSegment[] {
  const segments: ReadablePathSegment[] = [];
  let current: ItemData | undefined = node;
  while (current && current.depth > 0) {
    const { parent } = current;
    const isArrayIndex = !!parent && parent.type === ItemType.ARRAY;
    segments.unshift({
      label: isArrayIndex ? `[${current.key}]` : current.key,
      isArrayIndex,
    });
    current = parent;
  }
  return segments;
}

// Keep the exact JSON-style path available as a tooltip / accessible label.
function formatReadablePath(node: ItemData): string {
  return `$${getReadablePathSegments(node)
    .map((segment) =>
      segment.isArrayIndex ? segment.label : `.${segment.label}`
    )
    .join("")}`;
}

function isOverflowScrollable(node: HTMLElement): boolean {
  if (typeof window === "undefined" || !window.getComputedStyle) {
    return false;
  }
  const overflowY = window.getComputedStyle(node).overflowY;
  return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
}

// Nearest real scroll container for `el`. Element Plus' scrollbar wrap is the
// viewport in every JsonTreeView usage; otherwise the first scrollable
// ancestor. Returns null so we never scroll the document / an outer dialog.
function findScrollContainer(el: HTMLElement | null): HTMLElement | null {
  let node: HTMLElement | null = el ? el.parentElement : null;
  while (node && node !== document.body && node !== document.documentElement) {
    if (node.classList && node.classList.contains("el-scrollbar__wrap")) {
      return node;
    }
    if (node.scrollHeight - node.clientHeight > 1 && isOverflowScrollable(node)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

// Scroll only the active row's own container so the row sits just below the
// sticky toolbar. No-op when already visible or when no container exists.
function scrollActiveResultIntoView(
  activeEl: HTMLElement | null,
  toolbarEl: HTMLElement | null,
  margin = 8
): number {
  if (!activeEl) {
    return 0;
  }
  const container = findScrollContainer(activeEl);
  if (!container) {
    return 0;
  }
  const containerRect = container.getBoundingClientRect();
  const elRect = activeEl.getBoundingClientRect();
  const toolbarHeight = toolbarEl ? toolbarEl.offsetHeight : 0;
  const topBoundary = containerRect.top + toolbarHeight + margin;
  const bottomBoundary = containerRect.bottom - margin;
  let delta = 0;
  if (elRect.top < topBoundary) {
    delta = elRect.top - topBoundary;
  } else if (elRect.bottom > bottomBoundary) {
    delta = elRect.bottom - bottomBoundary;
  }

  // Do not leave a parent row half-visible below the sticky toolbar. Align the
  // nearest crossing row with the toolbar boundary so the tree context starts
  // on a clean row while the active result remains visible.
  if (delta !== 0) {
    const treeRoot = activeEl.closest(".jtv-container");
    const rows = treeRoot?.querySelectorAll<HTMLElement>(".data-key") || [];
    for (const row of rows) {
      if (row === activeEl) {
        continue;
      }
      const rowRect = row.getBoundingClientRect();
      const futureTop = rowRect.top - delta;
      const futureBottom = rowRect.bottom - delta;
      if (futureTop < topBoundary && futureBottom > topBoundary) {
        delta +=
          delta > 0
            ? futureBottom - topBoundary
            : futureTop - topBoundary;
        break;
      }
    }
  }

  if (delta !== 0) {
    const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollTop = Math.max(0, Math.min(container.scrollTop + delta, maxScroll));
  }
  return delta;
}

type Props = {
  data?: string;
  rootKey: string;
  maxDepth: number;
  colorScheme: string;
  objectMap: Map<any, any>;
  tab: number;
  path: string;
  element: SelectedElement;
  clickAbleDataType: "normal" | "branched";
  searchable: boolean;
  searchData?: string;
};

const DEBOUNCE_MS = 250;

export default defineComponent({
  name: "JsonTreeView",
  components: { JsonTreeViewItem, ElInput, ElButton, ElButtonGroup, ElTooltip },
  props: {
    data: {
      type: String,
      required: false,
    },
    rootKey: {
      type: String,
      required: false,
      default: "/",
    },
    maxDepth: {
      type: Number,
      required: false,
      default: 1,
    },
    colorScheme: {
      type: String,
      required: false,
      default: "light",
      validator: (value: string) => ["light", "dark"].indexOf(value) !== -1,
    },
    objectMap: {
      type: Map,
      required: false,
      default: new Map(),
    },
    tab: {
      type: Number,
      required: false,
      default: 1,
    },
    path: {
      type: String,
      required: false,
      default: "path",
    },
    element: {
      type: Object as PropType<SelectedElement>,
      required: false,
      default: {
        key: "key",
        value: "value",
      },
    },
    clickAbleDataType: {
      type: String,
      default: "normal",
    },
    // Whether the UI-only search experience is shown. Defaults to false so
    // existing usages are unchanged.
    searchable: {
      type: Boolean,
      required: false,
      default: false,
    },
    // The complete output used for searching. When omitted, search falls back
    // to `data` (only useful when `data` already contains the full output).
    searchData: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  setup(rawProps, context) {
    const props = rawProps as unknown as Props;
    const { t } = i18n.global;

    const query = ref("");
    const debouncedQuery = ref("");
    const activeIndex = ref(0);
    const containerRef = ref<HTMLElement | null>(null);
    const toolbarRef = ref<HTMLElement | null>(null);
    const pathRef = ref<HTMLElement | null>(null);
    const pathMeasureRef = ref<HTMLElement | null>(null);
    const isPathTruncated = ref(false);
    const isPathVisuallyTruncated = ref(false);
    const isPopupContext = ref(false);
    // Adaptive indentation density. `indentStep` is the per-level indent (px)
    // and `maxIndentLevels` is the depth past which indentation stops growing,
    // so deep + narrow trees never squeeze content below a readable minimum.
    // Both are derived from the live container width via a ResizeObserver.
    const indentStep = ref(12);
    const maxIndentLevels = ref(999);
    const isNarrow = ref(false);
    // Deeply-nested items read the cap without prop drilling.
    provide("jtvMaxIndentLevels", maxIndentLevels);
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let toolbarResizeObserver: {
      observe: (element: Element) => void;
      disconnect: () => void;
    } | null = null;
    let scrollbarBar: HTMLElement | null = null;
    let originalScrollbarTop = "";
    let pathResizeObserver: {
      observe: (element: Element) => void;
      disconnect: () => void;
    } | null = null;
    let densityResizeObserver: {
      observe: (element: Element) => void;
      disconnect: () => void;
    } | null = null;

    // Readable content floor (px) that indentation must never eat into, plus the
    // chevron gutter each row already reserves. The remaining width is the indent
    // budget; the cap = budget / step so total indent stays within it.
    const CONTENT_MIN_WIDTH = 160;
    const NARROW_WIDTH = 360;
    const STEP_MIN = 3;
    const STEP_MAX = 14;

    function updateDensity(): void {
      const el = containerRef.value;
      if (!el) {
        return;
      }
      const width = el.clientWidth;
      if (!width) {
        return;
      }
      const narrow = width < NARROW_WIDTH;
      isNarrow.value = narrow;
      // Fit ~16 levels into ~45% of the width, then clamp to a sane range.
      const step = Math.max(
        STEP_MIN,
        Math.min(STEP_MAX, Math.floor((width * 0.45) / 16))
      );
      indentStep.value = step;
      const chevronGutter = narrow ? 26 : 40;
      const indentBudget = Math.max(0, width - CONTENT_MIN_WIDTH - chevronGutter);
      // Only as many levels as the budget actually supports — never force a
      // minimum, or total indentation would exceed the budget and eat into the
      // readable content floor (the exact case narrow panels need protected).
      maxIndentLevels.value = Math.max(0, Math.floor(indentBudget / step));
    }

    function attachDensityObserver(el: HTMLElement | null): void {
      densityResizeObserver?.disconnect();
      densityResizeObserver = null;
      if (!el) {
        return;
      }
      updateDensity();
      const ResizeObserverConstructor =
        typeof window !== "undefined"
          ? (window as any).ResizeObserver
          : undefined;
      if (ResizeObserverConstructor) {
        densityResizeObserver = new ResizeObserverConstructor(updateDensity);
        densityResizeObserver.observe(el);
      }
    }

    watch(containerRef, attachDensityObserver, { flush: "post" });

    function clearScrollbarTrackOffset(): void {
      toolbarResizeObserver?.disconnect();
      toolbarResizeObserver = null;
      if (scrollbarBar) {
        scrollbarBar.style.top = originalScrollbarTop;
        scrollbarBar = null;
        originalScrollbarTop = "";
      }
    }

    // Element Plus overlays its vertical bar on the whole scroll viewport.
    // Move only that bar below the search toolbar so the toolbar can use the
    // complete panel width. ResizeObserver also covers the optional path row.
    function attachScrollbarTrackOffset(toolbar: HTMLElement | null): void {
      clearScrollbarTrackOffset();
      isPopupContext.value = !!toolbar?.closest(".modal-body.content-body");
      if (!toolbar) {
        return;
      }
      const root = toolbar.closest(".el-scrollbar") as HTMLElement | null;
      if (!root) {
        return;
      }
      const verticalBar = root.querySelector<HTMLElement>(
        ".el-scrollbar__bar.is-vertical"
      );
      if (!verticalBar) {
        return;
      }
      scrollbarBar = verticalBar;
      originalScrollbarTop = verticalBar.style.top;
      const syncOffset = (): void => {
        verticalBar.style.top = `${toolbar.offsetHeight + 2}px`;
      };
      syncOffset();
      const ResizeObserverConstructor =
        typeof window !== "undefined"
          ? (window as any).ResizeObserver
          : undefined;
      if (ResizeObserverConstructor) {
        toolbarResizeObserver = new ResizeObserverConstructor(syncOffset);
        toolbarResizeObserver.observe(toolbar);
      }
    }

    watch(toolbarRef, attachScrollbarTrackOffset, { flush: "post" });

    function updatePathTooltipVisibility(): void {
      const pathElement = pathRef.value;
      if (!pathElement) {
        isPathVisuallyTruncated.value = false;
        return;
      }
      const segments = pathElement.querySelectorAll<HTMLElement>(
        ".jtv-search-path-segment:not(.jtv-search-path-segment--ellipsis)"
      );
      isPathVisuallyTruncated.value =
        pathElement.scrollWidth > pathElement.clientWidth ||
        Array.from(segments).some(
          (segment) => segment.scrollWidth > segment.clientWidth
        );
    }

    function updatePathLayout(): void {
      const pathElement = pathRef.value;
      const measureElement = pathMeasureRef.value;
      if (!pathElement || !measureElement) {
        isPathTruncated.value = false;
        isPathVisuallyTruncated.value = false;
        return;
      }

      const requiredWidth = Math.ceil(
        measureElement.getBoundingClientRect().width ||
          measureElement.scrollWidth
      );
      const availableWidth = pathElement.clientWidth;
      const shouldTruncate =
        activePathSegments.value.length > 3 &&
        requiredWidth > availableWidth + 1;

      if (isPathTruncated.value !== shouldTruncate) {
        isPathTruncated.value = shouldTruncate;
        nextTick(updatePathTooltipVisibility);
        return;
      }
      updatePathTooltipVisibility();
    }

    function attachPathResizeObserver(pathElement: HTMLElement | null): void {
      pathResizeObserver?.disconnect();
      pathResizeObserver = null;
      if (!pathElement) {
        isPathVisuallyTruncated.value = false;
        return;
      }
      nextTick(updatePathLayout);
      const ResizeObserverConstructor =
        typeof window !== "undefined"
          ? (window as any).ResizeObserver
          : undefined;
      if (ResizeObserverConstructor) {
        pathResizeObserver = new ResizeObserverConstructor(
          updatePathLayout
        );
        pathResizeObserver.observe(pathElement);
        if (pathMeasureRef.value) {
          pathResizeObserver.observe(pathMeasureRef.value);
        }
      }
    }

    watch(
      [pathRef, pathMeasureRef],
      ([pathElement]) => attachPathResizeObserver(pathElement),
      { flush: "post" }
    );
    onMounted(() => {
      window.addEventListener("resize", updatePathLayout);
      window.addEventListener("resize", updateDensity);
    });

    function itemSelected(data: any): void {
      context.emit("selected", data);
    }

    // Visible (possibly lazy-loaded) tree. `data` is already display-ready, so
    // numbers are not re-quoted here.
    const parsed = computed((): ItemData => parseToTree(props.data, props.rootKey, false));

    const map = computed(() => props.objectMap);

    // Complete tree used only for searching. The raw output is normalized the
    // same way the lazy renderer normalizes its chunks so the revealed branch
    // matches the visible tree.
    const searchTree = computed((): ItemData | null => {
      if (!props.searchable) {
        return null;
      }
      const source =
        props.searchData !== undefined &&
        props.searchData !== null &&
        props.searchData !== ""
          ? props.searchData
          : props.data;
      if (source === undefined || source === null) {
        return null;
      }
      return parseToTree(source, props.rootKey, true);
    });

    const matches = computed(() =>
      collectMatches(searchTree.value, debouncedQuery.value)
    );

    const isSearchActive = computed(
      () => props.searchable && debouncedQuery.value.trim() !== ""
    );
    const hasResults = computed(() => matches.value.length > 0);

    const safeIndex = computed(() =>
      matches.value.length === 0
        ? 0
        : Math.min(activeIndex.value, matches.value.length - 1)
    );
    const activeMatch = computed(() =>
      hasResults.value ? matches.value[safeIndex.value] : null
    );
    const activePath = computed(() =>
      activeMatch.value ? activeMatch.value.path : ""
    );
    // Rendered tree = the normal lazy-loaded tree, plus (only when required)
    // the single top-level branch needed to reveal the active match. Nothing is
    // ever filtered out: non-matching and lazily-loaded nodes always remain.
    const renderTree = computed((): ItemData => {
      const base = parsed.value;
      if (!isSearchActive.value || !activeMatch.value || !base.children) {
        return base;
      }
      // Walk up to the top-level (depth 1) ancestor of the active match.
      let branchTop: ItemData | undefined = activeMatch.value.node;
      while (branchTop && branchTop.depth > 1) {
        branchTop = branchTop.parent;
      }
      if (!branchTop || branchTop.depth !== 1) {
        return base; // Match is the root/scalar – nothing to merge.
      }
      const top = branchTop;
      const alreadyVisible = base.children.some(
        (child) => child.path === top.path
      );
      if (alreadyVisible) {
        return base;
      }
      // Union: keep the whole existing visible tree and append only the branch
      // required to reveal the active result.
      return { ...base, children: [...base.children, top] };
    });

    // Ancestor node paths of the active match. Used to force-open exactly the
    // active result's ancestor chain (an overlay that never collapses anything).
    const expandedPaths = computed((): Set<string> => {
      const paths = new Set<string>();
      if (!isSearchActive.value || !activeMatch.value) {
        return paths;
      }
      let node: ItemData | undefined = activeMatch.value.node.parent;
      while (node) {
        paths.add(node.path);
        node = node.parent;
      }
      return paths;
    });

    const activePathLabel = computed(() =>
      activeMatch.value ? formatReadablePath(activeMatch.value.node) : ""
    );
    const activePathSegments = computed(() =>
      activeMatch.value
        ? getReadablePathSegments(activeMatch.value.node)
        : []
    );
    const shouldShowPathTooltip = computed(
      () => isPathTruncated.value || isPathVisuallyTruncated.value
    );
    const visiblePathSegments = computed((): ReadablePathSegment[] => {
      const segments = activePathSegments.value;
      if (!isPathTruncated.value) {
        return segments;
      }
      return [
        segments[0],
        { label: "…", isArrayIndex: false, isEllipsis: true },
        ...segments.slice(-2),
      ];
    });

    const countLabel = computed(() => {
      const total = matches.value.length;
      const current = total === 0 ? 0 : safeIndex.value + 1;
      return `${current} / ${total}`;
    });

    function scrollActiveIntoView(): void {
      nextTick(() => {
        const root = containerRef.value;
        if (!root) {
          return;
        }
        const el = root.querySelector(".jtv-node--active") as HTMLElement | null;
        if (!el) {
          return;
        }
        // Scroll only the active result's own scroll container, keeping the row
        // below the sticky toolbar — never the outer dialog or the page.
        scrollActiveResultIntoView(el, toolbarRef.value);
      });
    }

    watch(
      () => query.value,
      (val) => {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
          debounceTimer = null;
        }
        if (val === "") {
          // Clearing must restore the normal lazy view immediately.
          debouncedQuery.value = "";
          activeIndex.value = 0;
          return;
        }
        debounceTimer = setTimeout(() => {
          debouncedQuery.value = val;
          activeIndex.value = 0;
          debounceTimer = null;
        }, DEBOUNCE_MS);
      }
    );

    // Keep the active index valid when the result set changes.
    watch(
      () => matches.value.length,
      (len) => {
        if (activeIndex.value > len - 1) {
          activeIndex.value = 0;
        }
      }
    );

    // Reveal + scroll whenever the active result changes.
    watch(activePath, () => {
      scrollActiveIntoView();
      nextTick(updatePathLayout);
    });

    function goNext(): void {
      if (!hasResults.value) {
        return;
      }
      activeIndex.value = (safeIndex.value + 1) % matches.value.length;
    }

    function goPrev(): void {
      if (!hasResults.value) {
        return;
      }
      activeIndex.value =
        (safeIndex.value - 1 + matches.value.length) % matches.value.length;
    }

    function onEnterKey(event: KeyboardEvent): void {
      if (event.shiftKey) {
        goPrev();
      } else {
        goNext();
      }
    }

    function clearSearch(): void {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      query.value = "";
      debouncedQuery.value = "";
      activeIndex.value = 0;
    }

    onBeforeUnmount(() => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      window.removeEventListener("resize", updatePathLayout);
      window.removeEventListener("resize", updateDensity);
      pathResizeObserver?.disconnect();
      densityResizeObserver?.disconnect();
      clearScrollbarTrackOffset();
    });

    return {
      t,
      query,
      debouncedQuery,
      itemSelected,
      parsed,
      map,
      containerRef,
      toolbarRef,
      pathRef,
      pathMeasureRef,
      isPopupContext,
      indentStep,
      isNarrow,
      isSearchActive,
      hasResults,
      activePath,
      renderTree,
      expandedPaths,
      activePathLabel,
      activePathSegments,
      splitIdentifier,
      isPathTruncated,
      shouldShowPathTooltip,
      visiblePathSegments,
      countLabel,
      goNext,
      goPrev,
      onEnterKey,
      clearSearch,
    };
  },
});
